import { useNavigate } from 'react-router-dom';
import PageNavbar from '../../../components/feature/PageNavbar';

export default function GraspPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <PageNavbar />

      {/* Hero Section */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Interactive Rehabilitation Table
              </span>
              <h1 className="text-5xl font-bold text-gray-900 mt-3 mb-6">
                GRASP Interactive Training System
              </h1>
              <p className="text-lg text-gray-700 leading-relaxed">
                GRASP is an innovative touchscreen rehabilitation table that combines physical therapy with engaging digital exercises. It provides interactive training for upper extremity function, cognitive skills, and fine motor control through intuitive touch-based activities.
              </p>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/a968bd3f33912ea44dc7a3f0569e57e4.jpg"
                alt="GRASP Interactive Training System"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              System Overview
            </h2>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              The GRASP system features a spacious interactive surface that allows for natural arm and hand movements, accommodating various reach distances and movement patterns. Its large touchscreen interface creates an engaging environment where patients can perform therapeutic exercises that feel more like games than traditional therapy.
            </p>

            <p className="text-gray-700 leading-relaxed mb-12">
              An extensive collection of therapeutic games and activities targets different motor and cognitive skills at various difficulty levels. Immediate visual and auditory feedback helps patients understand their performance and motivates continued effort, while exercises can be easily modified to match patient abilities, ensuring appropriate challenge levels throughout recovery.
            </p>

            <div className="border-l-4 border-gray-300 pl-6 my-12">
              <p className="text-lg text-gray-700 italic leading-relaxed">
                The system can accommodate multiple patients with individual profiles, tracking progress separately for each user. Detailed performance reports and analytics help therapists evaluate progress and adjust treatment plans effectively.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
              Training Capabilities
            </h2>

            <div className="grid md:grid-cols-2 gap-x-12 gap-y-8 mb-12">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Fine Motor Skills
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Precision touch exercises that improve finger dexterity, hand-eye coordination, and controlled movements.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Reaching & Grasping
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Activities that promote arm extension, reaching accuracy, and functional grasping patterns.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Cognitive Training
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Memory, attention, and problem-solving exercises that combine cognitive challenges with motor tasks.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Visual Perception
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Exercises targeting visual scanning, spatial awareness, and visual-motor integration skills.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Reaction Time
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Speed-based activities that improve response time, processing speed, and quick decision-making.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Movement Patterns
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Guided movement sequences that promote smooth, coordinated arm and hand movements.
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
              Suitable Conditions
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              The GRASP system is effective for a wide range of neurological and orthopedic conditions:
            </p>

            <ul className="space-y-3 text-gray-700 leading-relaxed mb-12">
              <li><strong>Stroke Rehabilitation</strong> — Helps stroke survivors regain upper limb function and cognitive abilities through engaging interactive exercises</li>
              <li><strong>Traumatic Brain Injury</strong> — Supports recovery of motor and cognitive functions with adjustable difficulty levels matching recovery stages</li>
              <li><strong>Parkinson's Disease</strong> — Improves fine motor control, coordination, and cognitive function through repetitive, goal-oriented activities</li>
              <li><strong>Multiple Sclerosis</strong> — Maintains and enhances upper extremity function and cognitive abilities with customizable exercise programs</li>
              <li><strong>Orthopedic Conditions</strong> — Facilitates recovery from upper limb injuries and surgeries through progressive, controlled exercises</li>
              <li><strong>Pediatric Rehabilitation</strong> — Engaging games make therapy fun for children while developing motor skills and cognitive abilities</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-8 px-6 border-t border-gray-200">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Accelerated Recovery Centre. All rights reserved.
          </p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="nofollow noopener noreferrer"
            className="text-gray-400 text-xs hover:text-gray-600 transition-colors mt-2 inline-block"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
